import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCatalog } from '../app/providers/CatalogProvider.jsx';
import { VIM_MEDIA_LOGO_URL } from '../services/media/mediaUrl.js';
import { withOpenAICampaignParams } from '../services/ads/openaiMeasurement.js';
import { MembershipPlans, DeviceGuide } from '../components/commerce/MembershipPlans.jsx';
import '../styles/subscriber-campaign.css';
import '../styles/subscriber-campaign-mobile-fit.css';

function posterArtwork(item) {
  return item?.image || '';
}

function CampaignArtwork({ item, className = '' }) {
  const artwork = posterArtwork(item);
  const classes = ['subscriber-artwork', 'is-poster', className]
    .filter(Boolean)
    .join(' ');

  return (
    <article className={classes} aria-label={item?.title || 'WatchVIM featured title'}>
      {artwork ? <img src={artwork} alt="" loading="lazy" decoding="async" /> : <div className="subscriber-artwork-fallback" />}
      <div className="subscriber-artwork-shade" />
      <div className="subscriber-artwork-label">
        <strong>{item?.title || 'WatchVIM'}</strong>
        <span>{item?.original ? 'WatchVIM Original' : (item?.kind === 'series' ? 'Series' : 'Film')}</span>
      </div>
    </article>
  );
}

function Benefit({ icon, title, children }) {
  return (
    <article className="subscriber-benefit">
      <span className="subscriber-benefit-icon" aria-hidden="true">{icon}</span>
      <div>
        <h3>{title}</h3>
        <p>{children}</p>
      </div>
    </article>
  );
}

export function SubscriberCampaignPage() {
  const { catalog } = useCatalog();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const featured = useMemo(() => {
    const preferred = (catalog.featured || []).filter((item) => posterArtwork(item));
    const originals = (catalog.originals || []).filter((item) => posterArtwork(item));
    const all = (catalog.titles || []).filter((item) => posterArtwork(item));
    const seen = new Set();

    return [...preferred, ...originals, ...all].filter((item) => {
      if (!item?.id || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    }).slice(0, 12);
  }, [catalog.featured, catalog.originals, catalog.titles]);

  const heroItems = featured.slice(0, 6);
  const railItems = featured.slice(0, 6);
  const primaryPath = withOpenAICampaignParams('/membership');
  const browsePath = withOpenAICampaignParams('/movies');
  const campaignLinks = [
    [withOpenAICampaignParams('/home'), 'Browse'],
    [withOpenAICampaignParams('/movies'), 'Films'],
    [withOpenAICampaignParams('/shows'), 'Series'],
    [withOpenAICampaignParams('/originals'), 'Originals'],
  ];

  return (
    <div className="subscriber-campaign">
      <header className="subscriber-header">
        <Link to={primaryPath} className="subscriber-lockup" aria-label="Subscribe to WatchVIM">
          <span className="subscriber-now"><small>SUBSCRIBE</small><strong>NOW</strong></span>
          <img src={VIM_MEDIA_LOGO_URL} alt="VIM" className="subscriber-logo" decoding="async" />
          <span className="subscriber-url">WWW.WATCHVIM.COM</span>
        </Link>

        <nav className="subscriber-desktop-nav" aria-label="WatchVIM campaign navigation">
          {campaignLinks.map(([to, label]) => <Link key={to} to={to}>{label}</Link>)}
        </nav>

        <div className="subscriber-header-actions">
          <Link to={withOpenAICampaignParams('/login')} className="subscriber-signin">Sign In</Link>
          <Link to={primaryPath} className="subscriber-header-cta">Start Watching</Link>
          <button
            type="button"
            className="subscriber-mobile-menu-button"
            aria-label="Open WatchVIM menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            <span /><span /><span />
          </button>
        </div>

        {mobileMenuOpen ? (
          <nav className="subscriber-mobile-menu" aria-label="WatchVIM mobile campaign navigation">
            {campaignLinks.map(([to, label]) => <Link key={to} to={to} onClick={() => setMobileMenuOpen(false)}>{label}</Link>)}
            <Link to={primaryPath} className="subscriber-mobile-menu-cta" onClick={() => setMobileMenuOpen(false)}>Start Watching</Link>
          </nav>
        ) : null}
      </header>

      <main>
        <section className="subscriber-hero">
          <div className="subscriber-hero-top">
            <div className="subscriber-hero-copy">
              <span className="subscriber-eyebrow">Bold stories. Real voices. No limits.</span>
              <h1><span>Stream stories</span><em>that move different</em></h1>
              <p>Independent films. Black stories. LGBTQIA+ voices.<br className="subscriber-desktop-break" /> Original series. All in one place.</p>
              <div className="subscriber-price"><strong>Monthly or annual</strong><span>membership</span></div>
              <div className="subscriber-hero-actions">
                <Link to={primaryPath} className="subscriber-primary-cta">Start Watching Now</Link>
                <Link to={browsePath} className="subscriber-secondary-cta"><span aria-hidden="true">▶</span> Browse Titles</Link>
              </div>
            </div>

            <div className="subscriber-hero-mosaic" aria-label="Featured WatchVIM programming">
              {heroItems.length ? heroItems.map((item, index) => (
                <CampaignArtwork key={item.id} item={item} className={`mosaic-${index + 1}`} />
              )) : (
                <div className="subscriber-mosaic-loading"><img src={VIM_MEDIA_LOGO_URL} alt="VIM" /></div>
              )}
              <div className="subscriber-mosaic-brand" aria-label="WatchVIM">
                <img src={VIM_MEDIA_LOGO_URL} alt="VIM" />
              </div>
            </div>
          </div>

          <div className="subscriber-featured" aria-labelledby="subscriber-featured-heading">
            <div className="subscriber-section-heading">
              <h2 id="subscriber-featured-heading">Featured Titles</h2>
            </div>
            <div className="subscriber-featured-grid">
              {railItems.length
                ? railItems.map((item) => <CampaignArtwork key={item.id} item={item} />)
                : Array.from({ length: 6 }, (_, index) => <CampaignArtwork key={index} item={null} />)}
            </div>
          </div>
        </section>

        <section className="subscriber-membership" aria-label="Membership plans and devices"><h2>Choose your membership</h2><MembershipPlans campaign /><DeviceGuide /></section>

        <section className="subscriber-benefits" aria-label="Why WatchVIM">
          <Benefit icon="●" title="Diverse Stories">Spotlighting Black stories, LGBTQIA+ voices, and global perspectives.</Benefit>
          <Benefit icon="▣" title="Indie to Iconic">Independent films and original series you won’t find anywhere else.</Benefit>
          <Benefit icon="▱" title="Watch Anywhere">Stream in your browser or on supported Apple and Android devices. See the guide above.</Benefit>
          <Benefit icon="×" title="Cancel Anytime">Cancel future renewals anytime. Keep access through your paid period.</Benefit>
        </section>

        <section className="subscriber-final-cta">
          <div>
            <span>Bold stories.</span>
            <strong>Your next favorite is here.</strong>
          </div>
          <Link to={primaryPath} className="subscriber-primary-cta">Start Watching Now</Link>
        </section>
      </main>

      <footer className="subscriber-campaign-footer">
        <span>© VIM Media / WatchVIM</span>
        <div>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/support">Support</Link>
        </div>
      </footer>
    </div>
  );
}
